import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  LogOut,
  UserCircle2,
  ChevronDown,
  IdCard,
  BookOpen,
  LifeBuoy,
  Menu,
  Home as HomeIcon,
  Sparkles,
  BookOpenCheck,
  User2,
  LayoutDashboard,
  UserPlus,
} from "lucide-react";

const NavLink = ({ to, children, testid }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`px-3 py-2 rounded-full text-sm transition-colors ${
        active
          ? "text-[#0A4D3C] bg-[#F6F4EE]"
          : "text-[#5E5C58] hover:text-[#222222]"
      }`}
    >
      {children}
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Iteration 31 — User profile dropdown.
// Replaces the standalone "خروج" button with a clean dropdown anchored on the
// user's display name. Items: Account / Library / Support / Logout (the last
// styled in a soft rose accent so it's visually distinct from the rest).
// ---------------------------------------------------------------------------
const UserMenu = ({ user, onLogout, navigate }) => {
  const initial = (user.name || "ا").trim().charAt(0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-user-menu"
          className="flex items-center gap-2 rounded-full pl-3 pr-1 py-1 hover:bg-[#F6F4EE] focus:outline-none focus:ring-1 focus:ring-[#C5A059] transition-colors text-[#222222]"
        >
          <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate">
            {user.name}
          </span>
          <ChevronDown className="hidden sm:inline w-3.5 h-3.5 text-[#8B8780]" />
          <span
            aria-hidden
            className="w-9 h-9 rounded-full bg-[#0A4D3C] text-white grid place-items-center font-heading text-base"
          >
            {initial}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-60 rounded-2xl border-[#E5E1D8] bg-white/95 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(60,40,20,0.18)] p-1.5"
        data-testid="nav-user-menu-content"
        dir="rtl"
      >
        <DropdownMenuLabel className="px-3 py-2">
          <div className="text-xs text-[#8B8780]">مرحبًا</div>
          <div className="text-sm text-[#222222] font-medium truncate">{user.name}</div>
          <div className="text-[11px] text-[#8B8780] truncate" dir="ltr">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#F0EDE4]" />

        <DropdownMenuItem
          data-testid="nav-menu-account"
          onSelect={() => navigate("/account")}
          className="rounded-xl px-3 py-2.5 cursor-pointer text-sm text-[#222222] focus:bg-[#F6F4EE]"
        >
          <IdCard className="w-4 h-4 ms-2 text-[#0A4D3C]" />
          معلومات الحساب
        </DropdownMenuItem>

        <DropdownMenuItem
          data-testid="nav-menu-library"
          onSelect={() => navigate("/dashboard?tab=library")}
          className="rounded-xl px-3 py-2.5 cursor-pointer text-sm text-[#222222] focus:bg-[#F6F4EE]"
        >
          <BookOpen className="w-4 h-4 ms-2 text-[#0A4D3C]" />
          مكتبتي
        </DropdownMenuItem>

        <DropdownMenuItem
          data-testid="nav-menu-support"
          onSelect={() => {
            window.open("https://wa.me/966538278484", "_blank", "noopener");
          }}
          className="rounded-xl px-3 py-2.5 cursor-pointer text-sm text-[#222222] focus:bg-[#F6F4EE]"
        >
          <LifeBuoy className="w-4 h-4 ms-2 text-[#0A4D3C]" />
          الدعم الفني
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-[#F0EDE4]" />

        <DropdownMenuItem
          data-testid="nav-menu-logout"
          onSelect={() => {
            onLogout();
            navigate("/");
          }}
          className="rounded-xl px-3 py-2.5 cursor-pointer text-sm text-rose-700 focus:bg-rose-50 focus:text-rose-800"
        >
          <LogOut className="w-4 h-4 ms-2" />
          تسجيل الخروج
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------------------------------------------------------------------------
// Iteration 37 — Mobile hamburger drawer.
// Slides in from the right (RTL-friendly) on screens < md.
// All nav links + auth CTAs (when logged-out) live inside; closes
// automatically on link tap, on overlay click, on the close (X) icon, and
// on Escape.
// ---------------------------------------------------------------------------
const MobileNavDrawer = ({ user, onLogout, navigate }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const link = (to, label, Icon, testid) => {
    const active = pathname === to;
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={() => go(to)}
        className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-right transition-colors ${
          active
            ? "bg-[#0A4D3C] text-[#FAF9F6]"
            : "bg-[#F6F4EE] text-[#222222] hover:bg-[#EFEAE0]"
        }`}
      >
        <Icon
          className={`w-5 h-5 shrink-0 ${
            active ? "text-[#C5A059]" : "text-[#0A4D3C]"
          }`}
        />
        <span className="text-base font-medium flex-1">{label}</span>
      </button>
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="فتح القائمة"
          data-testid="nav-mobile-trigger"
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-[#0A4D3C] hover:bg-[#F6F4EE] focus:outline-none focus:ring-1 focus:ring-[#C5A059] transition-colors"
        >
          <Menu className="w-6 h-6" />
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        dir="rtl"
        data-testid="nav-mobile-drawer"
        className="w-[88%] sm:w-[420px] bg-[#FAF9F6] border-l border-[#E5E1D8] p-0 flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-[#EFEAE0] text-right">
          <SheetTitle className="text-right">
            <img
              src="https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/60nvz0lu_%D8%A3%D9%86%D8%A7%20%D8%A7%D9%84%D8%AE%D8%A7%D9%84%D8%AF%D9%8A%20%28%D8%A7%D9%84%D9%84%D9%88%D9%82%D9%88%20%D8%A7%D9%84%D9%86%D9%87%D8%A7%D8%A6%D9%8A%29.png"
              alt="أنا الخالدي"
              className="h-20 w-auto object-contain select-none"
              draggable="false"
            />
          </SheetTitle>
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {link("/", "الرئيسية", HomeIcon, "mnav-home")}
          {link("/services", "الجلسات", Sparkles, "mnav-services")}
          {link("/book", "الكتاب الرقمي", BookOpenCheck, "mnav-book")}
          {link("/about", "عن المؤلفة", User2, "mnav-about")}
          {user && user.role !== "admin" &&
            link("/dashboard", "حسابي", IdCard, "mnav-dashboard")}
          {user?.role === "admin" &&
            link("/admin", "لوحة التحكم", LayoutDashboard, "mnav-admin")}
        </nav>

        <div className="px-5 py-4 border-t border-[#EFEAE0] bg-white/60">
          {user ? (
            <button
              type="button"
              data-testid="mnav-logout"
              onClick={() => {
                setOpen(false);
                onLogout();
                navigate("/");
              }}
              className="w-full flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-rose-700 hover:bg-rose-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              تسجيل الخروج
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <Button
                data-testid="mnav-login"
                onClick={() => go("/login")}
                className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-12 text-base"
              >
                <UserCircle2 className="w-4 h-4 ms-2" />
                تسجيل الدخول
              </Button>
              <Button
                data-testid="mnav-register"
                variant="outline"
                onClick={() => go("/register")}
                className="rounded-full border-[#0A4D3C] text-[#0A4D3C] hover:bg-[#0A4D3C]/5 h-12 text-base"
              >
                <UserPlus className="w-4 h-4 ms-2" />
                إنشاء حساب
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-[#E5E1D8]">
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 h-20 sm:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-logo" className="flex items-center h-full py-1.5">
          <img
            src="https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/60nvz0lu_%D8%A3%D9%86%D8%A7%20%D8%A7%D9%84%D8%AE%D8%A7%D9%84%D8%AF%D9%8A%20%28%D8%A7%D9%84%D9%84%D9%88%D9%82%D9%88%20%D8%A7%D9%84%D9%86%D9%87%D8%A7%D8%A6%D9%8A%29.png"
            alt="أنا الخالدي"
            className="h-14 sm:h-[72px] w-auto max-h-full object-contain select-none"
            draggable="false"
          />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <NavLink to="/" testid="nav-home">الرئيسية</NavLink>
          <NavLink to="/services" testid="nav-services">الجلسات</NavLink>
          <NavLink to="/book" testid="nav-book">الكتاب الرقمي</NavLink>
          <NavLink to="/about" testid="nav-about">عن المؤلفة</NavLink>
          {user && user.role !== "admin" && <NavLink to="/dashboard" testid="nav-dashboard">حسابي</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" testid="nav-admin">لوحة التحكم</NavLink>}
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <UserMenu user={user} onLogout={logout} navigate={navigate} />
          ) : (
            <>
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                className="hidden md:inline-flex rounded-full"
                onClick={() => navigate("/login")}
              >
                <UserCircle2 className="w-4 h-4 ms-2" />
                تسجيل الدخول
              </Button>
              <Button
                data-testid="nav-register-btn"
                className="hidden md:inline-flex rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
                onClick={() => navigate("/register")}
              >
                إنشاء حساب
              </Button>
            </>
          )}
          <MobileNavDrawer user={user} onLogout={logout} navigate={navigate} />
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
